import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import { adminLinkView, type AdminLinkView } from "./vocab.ts";

/**
 * The admin deep-link element (task 11.5; design 12.2 "the admin deep-link element"; FR-1.5). This
 * is the task's named, testable behaviour: a control that links out to a service's external admin
 * console, built from the source's registered `admin_url`, with THREE distinct states.
 *
 * The three states (derived in `adminLinkView` from the contract's `AdminLink` union) are all
 * present in the DOM — the element is NEVER hidden — but visibly and semantically distinct:
 *
 *  1. UNKNOWN (default): registration status is not yet known (loading, or `registration:"unknown"`).
 *     Rendered as a DISABLED placeholder `<button>` with `aria-disabled` and `data-state="unknown"`,
 *     whose accessible name says the status is not known yet. Not a link, not clickable.
 *  2. REGISTERED: a non-empty `admin_url` is known. Rendered as an ENABLED `<a>` to that url, opened
 *     in a new tab with `rel="noopener noreferrer"` and `data-state="registered"`.
 *  3. NONE: registration is known to carry no url. Rendered as a DISABLED placeholder `<button>`
 *     with the visible text "none configured" and `data-state="none"`.
 *
 * States 1 and 3 are both disabled and look similar, so they are kept DISTINCT in the DOM
 * (`data-state`) and in the accessible name — "unknown" vs "none configured" — because they mean
 * different things to an operator and the test checks all three.
 *
 * The url is UNTRUSTED external data (it comes from a source's registration). The component never
 * auto-navigates to it and never injects it into script; it only renders it as the `href` of an
 * anchor the operator clicks, and only when its scheme is a safe, http(s) one. A url with any other
 * scheme (`javascript:`, `data:`, …) is refused and the element falls back to the disabled
 * "unknown" placeholder rather than render a dangerous link.
 */

type AdminLink = components["schemas"]["AdminLink"];

export interface AdminDeepLinkProps {
  /** The service's admin-link registration from the response, or `undefined` while unknown. */
  readonly link: AdminLink | undefined;
  /** The service label, woven into the accessible name (e.g. "AIS admin console"). */
  readonly serviceLabel: string;
}

export function AdminDeepLink(props: AdminDeepLinkProps): ReactNode {
  // Derive the tri-state, then guard the registered url against unsafe schemes before trusting it
  // as an href. An unsafe or unparseable url degrades to the disabled "unknown" placeholder.
  let view: AdminLinkView = adminLinkView(props.link);
  if (view.kind === "registered" && !isSafeHttpUrl(view.url)) {
    view = { kind: "unknown" };
  }

  const name = `${props.serviceLabel} admin console`;

  if (view.kind === "registered") {
    return (
      <a
        className="svc-admin"
        data-state="registered"
        href={view.url}
        target="_blank"
        // Untrusted external url: isolate the opened tab and strip the referrer (design 12 safety).
        rel="noopener noreferrer"
        aria-label={`Open the ${name} (external)`}
        title={view.url}
      >
        <span className="svc-admin__label">{name}</span>
        <span className="svc-admin__hint" aria-hidden="true">
          ↗ open
        </span>
      </a>
    );
  }

  // Both remaining states are a DISABLED placeholder. They differ in text, data-state and the
  // accessible name so "status not known yet" is never confused with "known to have none".
  const isNone = view.kind === "none";
  const hint = isNone ? "none configured" : "unavailable";
  const ariaLabel = isNone
    ? `${name}: none configured`
    : `${name}: registration status not known yet`;

  return (
    <button
      type="button"
      className="svc-admin"
      data-state={view.kind}
      disabled
      aria-disabled="true"
      aria-label={ariaLabel}
    >
      <span className="svc-admin__label">{name}</span>
      <span className="svc-admin__hint">{hint}</span>
    </button>
  );
}

/**
 * True when `value` parses as an absolute `http(s)` url. The contract constrains `admin_url` to an
 * `https` uri, but the url is still untrusted data at the client, so we re-check here: only `http`
 * and `https` schemes are allowed, so a `javascript:` / `data:` / other-scheme string is never
 * rendered as a clickable link.
 */
function isSafeHttpUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === "https:" || parsed.protocol === "http:";
}
